import React, { useEffect } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { SwiperOptions } from "swiper/types"
import SwiperClass from "swiper/types/swiper-class"
import SlidePosition, { SlideType } from "./SlidePosition"
import "swiper/css"
import "swiper/css/pagination"
import styles from "./Carousel.module.css"
import { ThemeColor, classNames } from "@punks/ui-core"

export interface CarouselSlide {
  content: React.ReactNode
  className?: string
}

export interface CarouselResponsiveSettingsItem {
  spaceBetween?: number
  slidesPerView?: number
  slidesOffsetBefore?: number
  slidesOffsetAfter?: number
}

export interface CarouselResponsiveSettings {
  sm?: CarouselResponsiveSettingsItem
  md?: CarouselResponsiveSettingsItem
  lg?: CarouselResponsiveSettingsItem
  xl?: CarouselResponsiveSettingsItem
  "2xl"?: CarouselResponsiveSettingsItem
}

export interface CustomBreakpointsOptions {
  [width: number]: SwiperOptions
}

export interface CarouselProps {
  bars?: SlideType
  barsColor?: ThemeColor
  slides: CarouselSlide[]
  defaultSettings: CarouselResponsiveSettingsItem
  responsiveSettings?: CarouselResponsiveSettings
  customBreakpoints?: CustomBreakpointsOptions
  overflowVisible?: boolean
  slideCentered?: boolean
}

const DEFAULT_BREAKPOINT_OPTIONS = {
  spaceBetween: 32,
  slidesPerView: 1,
  slidesOffsetBefore: 0,
  slidesOffsetAfter: 0,
}

const createBreakpointOptions = (
  defaultSettings: CarouselResponsiveSettingsItem,
  responsiveSettings?: CarouselResponsiveSettings,
  customBreakpoints?: CustomBreakpointsOptions
):
  | {
      [width: number]: SwiperOptions
    }
  | undefined => {
  if (customBreakpoints) {
    return customBreakpoints
  }
  if (responsiveSettings) {
    return {
      640: {
        spaceBetween:
          responsiveSettings.sm?.spaceBetween ??
          defaultSettings?.spaceBetween ??
          DEFAULT_BREAKPOINT_OPTIONS.spaceBetween,
        slidesPerView:
          responsiveSettings.sm?.slidesPerView ??
          defaultSettings?.slidesPerView ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesPerView,
        slidesOffsetBefore:
          responsiveSettings.sm?.slidesOffsetBefore ??
          defaultSettings?.slidesOffsetBefore ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesOffsetBefore,
        slidesOffsetAfter:
          responsiveSettings.sm?.slidesOffsetAfter ??
          defaultSettings?.slidesOffsetAfter ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesOffsetAfter,
      },
      768: {
        spaceBetween:
          responsiveSettings.md?.spaceBetween ??
          responsiveSettings.sm?.spaceBetween ??
          defaultSettings?.spaceBetween ??
          DEFAULT_BREAKPOINT_OPTIONS.spaceBetween,
        slidesPerView:
          responsiveSettings.md?.slidesPerView ??
          responsiveSettings.sm?.slidesPerView ??
          defaultSettings?.slidesPerView ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesPerView,
        slidesOffsetBefore:
          responsiveSettings.md?.slidesOffsetBefore ??
          responsiveSettings.sm?.slidesOffsetBefore ??
          defaultSettings?.slidesOffsetBefore ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesOffsetBefore,
        slidesOffsetAfter:
          responsiveSettings.md?.slidesOffsetAfter ??
          responsiveSettings.sm?.slidesOffsetAfter ??
          defaultSettings?.slidesOffsetAfter ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesOffsetAfter,
      },
      1024: {
        spaceBetween:
          responsiveSettings.lg?.spaceBetween ??
          responsiveSettings.md?.spaceBetween ??
          responsiveSettings.sm?.spaceBetween ??
          defaultSettings?.spaceBetween ??
          DEFAULT_BREAKPOINT_OPTIONS.spaceBetween,
        slidesPerView:
          responsiveSettings.lg?.slidesPerView ??
          responsiveSettings.md?.slidesPerView ??
          responsiveSettings.sm?.slidesPerView ??
          defaultSettings?.slidesPerView ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesPerView,
        slidesOffsetBefore:
          responsiveSettings.lg?.slidesOffsetBefore ??
          responsiveSettings.md?.slidesOffsetBefore ??
          responsiveSettings.sm?.slidesOffsetBefore ??
          defaultSettings?.slidesOffsetBefore ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesOffsetBefore,
        slidesOffsetAfter:
          responsiveSettings.lg?.slidesOffsetAfter ??
          responsiveSettings.md?.slidesOffsetAfter ??
          responsiveSettings.sm?.slidesOffsetAfter ??
          defaultSettings?.slidesOffsetAfter ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesOffsetAfter,
      },
      1280: {
        spaceBetween:
          responsiveSettings.xl?.spaceBetween ??
          responsiveSettings.lg?.spaceBetween ??
          responsiveSettings.md?.spaceBetween ??
          responsiveSettings.sm?.spaceBetween ??
          defaultSettings?.spaceBetween ??
          DEFAULT_BREAKPOINT_OPTIONS.spaceBetween,
        slidesPerView:
          responsiveSettings.xl?.slidesPerView ??
          responsiveSettings.lg?.slidesPerView ??
          responsiveSettings.md?.slidesPerView ??
          responsiveSettings.sm?.slidesPerView ??
          defaultSettings?.slidesPerView ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesPerView,
        slidesOffsetBefore:
          responsiveSettings.xl?.slidesOffsetBefore ??
          responsiveSettings.lg?.slidesOffsetBefore ??
          responsiveSettings.md?.slidesOffsetBefore ??
          responsiveSettings.sm?.slidesOffsetBefore ??
          defaultSettings?.slidesOffsetBefore ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesOffsetBefore,
        slidesOffsetAfter:
          responsiveSettings.xl?.slidesOffsetAfter ??
          responsiveSettings.lg?.slidesOffsetAfter ??
          responsiveSettings.md?.slidesOffsetAfter ??
          responsiveSettings.sm?.slidesOffsetAfter ??
          defaultSettings?.slidesOffsetAfter ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesOffsetAfter,
      },
      1536: {
        spaceBetween:
          responsiveSettings["2xl"]?.spaceBetween ??
          responsiveSettings.xl?.spaceBetween ??
          responsiveSettings.lg?.spaceBetween ??
          responsiveSettings.md?.spaceBetween ??
          responsiveSettings.sm?.spaceBetween ??
          defaultSettings?.spaceBetween ??
          DEFAULT_BREAKPOINT_OPTIONS.spaceBetween,
        slidesPerView:
          responsiveSettings["2xl"]?.slidesPerView ??
          responsiveSettings.xl?.slidesPerView ??
          responsiveSettings.lg?.slidesPerView ??
          responsiveSettings.md?.slidesPerView ??
          responsiveSettings.sm?.slidesPerView ??
          defaultSettings?.slidesPerView ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesPerView,
        slidesOffsetBefore:
          responsiveSettings["2xl"]?.slidesOffsetBefore ??
          responsiveSettings.xl?.slidesOffsetBefore ??
          responsiveSettings.lg?.slidesOffsetBefore ??
          responsiveSettings.md?.slidesOffsetBefore ??
          responsiveSettings.sm?.slidesOffsetBefore ??
          defaultSettings?.slidesOffsetBefore ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesOffsetBefore,
        slidesOffsetAfter:
          responsiveSettings["2xl"]?.slidesOffsetAfter ??
          responsiveSettings.xl?.slidesOffsetAfter ??
          responsiveSettings.lg?.slidesOffsetAfter ??
          responsiveSettings.md?.slidesOffsetAfter ??
          responsiveSettings.sm?.slidesOffsetAfter ??
          defaultSettings?.slidesOffsetAfter ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesOffsetAfter,
      },
    }
  }

  return undefined
}

const Carousel = ({
  bars,
  barsColor,
  slides,
  defaultSettings,
  responsiveSettings,
  customBreakpoints,
  overflowVisible,
  slideCentered,
}: CarouselProps) => {
  const swiperRef = React.useRef<SwiperClass>()
  const [activeIndex, setActiveIndex] = React.useState(0)
  const [slidesPerView, setSlidesPerView] = React.useState(0)

  const handleSlideChange = (swiper: SwiperClass) => {
    setActiveIndex(swiper.activeIndex)
    setSlidesPerView(
      swiper.params.slidesPerView === "auto"
        ? 0
        : swiper.params.slidesPerView ?? 0
    )
  }

  const barsCount = slides.length - slidesPerView + 1

  return (
    <div>
      <Swiper
        className={classNames({
          "!overflow-y-visible": overflowVisible,
          [styles.slideCentered]: slideCentered,
        })}
        spaceBetween={
          defaultSettings?.spaceBetween ??
          DEFAULT_BREAKPOINT_OPTIONS.spaceBetween
        }
        slidesPerView={
          defaultSettings?.slidesPerView ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesPerView
        }
        slidesOffsetBefore={
          defaultSettings?.slidesOffsetBefore ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesOffsetBefore
        }
        slidesOffsetAfter={
          defaultSettings?.slidesOffsetAfter ??
          DEFAULT_BREAKPOINT_OPTIONS.slidesOffsetAfter
        }
        breakpoints={createBreakpointOptions(
          defaultSettings,
          responsiveSettings,
          customBreakpoints
        )}
        onSwiper={(swiper) => {
          swiperRef.current = swiper
          handleSlideChange(swiper)
        }}
        onActiveIndexChange={(swiper) => handleSlideChange(swiper)}
      >
        {slides.map((x, i) => (
          <SwiperSlide key={i} className={x.className}>
            {x.content}
          </SwiperSlide>
        ))}
      </Swiper>
      {bars && barsCount > 1 ? (
        <div className="flex items-center justify-center">
          <SlidePosition
            currentSlideNumber={activeIndex ?? 0}
            slidesCount={barsCount}
            type={bars}
            color={barsColor ?? "primary"}
          />
        </div>
      ) : undefined}
    </div>
  )
}

export default Carousel
